/**
 * Shoji Layout System
 *
 * Defines consistent horizontal alignment points across all Shoji panels.
 * This ensures that text content aligns properly regardless of panel type.
 */

export const SHOJI_LAYOUT = {
  // Main content alignment point - where text content should start
  CONTENT_ALIGNMENT: 40,

  // Header-specific adjustments
  HEADER: {
    // Use negative margin to pull text closer to button, accounting for button's internal padding
    CONTENT_OFFSET: 8,
  },

  // Schedule-specific elements
  SCHEDULE: {
    // Day divider line should end before content alignment point
    DAY_DIVIDER_END_OFFSET: 8,
    // Schedule content is flush left - individual elements handle their own spacing
    CONTENT_PADDING: 0,
    get DAY_DIVIDER_WIDTH() {
      return SHOJI_LAYOUT.CONTENT_ALIGNMENT - this.DAY_DIVIDER_END_OFFSET;
    }
  },

  // Other panel types can add their specific measurements here
  SEARCH: {
    // Future: search result alignment
  },

  CHAT: {
    // Chat-specific alignment for messages and input
    MESSAGE_LEFT_PADDING: 16,  // Comfortable left padding for chat messages
    AVATAR_SIZE: 32,
    AVATAR_MARGIN: 8,
    INPUT_PADDING: 16,
    // Total space for avatar + margin when avatars are enabled
    get AVATAR_TOTAL_WIDTH() {
      return this.AVATAR_SIZE + this.AVATAR_MARGIN;
    },
    // Message content starts after avatar when shown (avatar + margin from left edge)
    get MESSAGE_CONTENT_OFFSET() {
      return this.MESSAGE_LEFT_PADDING + this.AVATAR_TOTAL_WIDTH;
    }
  }
} as const;

/**
 * CSS Custom Properties for consistent alignment
 * These can be used in Tailwind classes or CSS-in-JS
 */
export const SHOJI_CSS_VARS = {
  '--shoji-content-alignment': `${SHOJI_LAYOUT.CONTENT_ALIGNMENT}px`,
  '--shoji-header-content-offset': `${SHOJI_LAYOUT.HEADER.CONTENT_OFFSET}px`,
  '--shoji-day-divider-width': `${SHOJI_LAYOUT.SCHEDULE.DAY_DIVIDER_WIDTH}px`,
  '--shoji-chat-avatar-size': `${SHOJI_LAYOUT.CHAT.AVATAR_SIZE}px`,
  '--shoji-chat-message-padding': `${SHOJI_LAYOUT.CHAT.MESSAGE_LEFT_PADDING}px`,
  '--shoji-chat-input-padding': `${SHOJI_LAYOUT.CHAT.INPUT_PADDING}px`,
} as const;

/**
 * Tailwind-compatible utility function for consistent spacing
 */
export function shojiSpacing(type: 'content' | 'header' | 'day-divider' | 'schedule-content' | 'chat-message' | 'chat-input') {
  switch (type) {
    case 'content':
      return { marginLeft: `${SHOJI_LAYOUT.CONTENT_ALIGNMENT}px` };
    case 'schedule-content':
      return { paddingLeft: `${SHOJI_LAYOUT.SCHEDULE.CONTENT_PADDING}px` };
    case 'header':
      return { marginLeft: `${SHOJI_LAYOUT.HEADER.CONTENT_OFFSET}px` };
    case 'day-divider':
      return { width: `${SHOJI_LAYOUT.SCHEDULE.DAY_DIVIDER_WIDTH}px` };
    case 'chat-message':
      return { paddingLeft: `${SHOJI_LAYOUT.CHAT.MESSAGE_LEFT_PADDING}px` };
    case 'chat-input':
      return { padding: `${SHOJI_LAYOUT.CHAT.INPUT_PADDING}px` };
    default:
      return {};
  }
}